import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CalendarOptions } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import { ErrorModalComponent } from './components/error-modal/error-modal.component';
import { TurnoModalComponent } from './components/turno-modal/turno-modal.component';
import { ApplicantSchedule, CalendarEvent, ClassType } from './models';
import { CalendarioTurnosService } from './service/calendario-turnos.service';
import interactionPlugin from '@fullcalendar/interaction';
import {
  clearStoredTrainingId,
  readStoredTrainingId,
} from '../../core/training-id-storage';
import { FormPending } from '../../formularios/models';
import { DynamicFormService } from '../../formularios/service/dynamic-form.service';

@Component({
  selector: 'app-calendario-turnos',
  templateUrl: './calendario-turnos.component.html',
  styleUrls: ['./calendario-turnos.component.scss'],
})
export class CalendarioTurnosComponent implements OnInit {
  calendarOptions: CalendarOptions = {};
  calendarEvents: CalendarEvent[] = [];
  applicantSchedule: ApplicantSchedule | null = null;
  isLoading = false;
  hasLoaded = false;
  trainingId: string | null = null;
  attendanceRegisteredDates = new Set<string>();
  readonly REST_DAY_TITLE = 'Día de descanso';
  readonly WORKDAY_TITLE = 'Día de trabajo';
  readonly INDUCTION_REMOTE_TITLE = 'Remoto';
  readonly INDUCTION_TITLE = 'Inducción';
  readonly ATTENDANCE_CLASS_TITLES: Record<string, ClassType> = {
    'inducción': ClassType.INDUCEMENT,
    '1° clase': ClassType.VEMO_UNI_CLASS_1,
    '2° clase': ClassType.VEMO_UNI_CLASS_2,
  };

  pendingForms: FormPending[] = [];
  pendingFormsLoaded = false;

  private initialDateApplied = false;

  constructor(
    public dialog: MatDialog,
    private calendarioTurnosService: CalendarioTurnosService,
    private dynamicFormService: DynamicFormService
  ) {}

  ngOnInit(): void {
    this.setCalendarConfig();
    this.trainingId = readStoredTrainingId();
    if (!this.trainingId) {
      this.hasLoaded = true;
      return;
    }
    this.getCalendarEvents(this.trainingId);
    this.loadPendingForms(this.trainingId);
  }

  formLabel(pending: FormPending): string {
    switch (pending.status) {
      case 'SUBMITTED':
        return `Ver ${pending.title}`;
      case 'DRAFT':
        return `Continuar ${pending.title}`;
      default:
        return `Completar ${pending.title}`;
    }
  }

  private loadPendingForms(trainingId: string): void {
    this.dynamicFormService.getPendingForms(Number(trainingId)).subscribe({
      next: (pendingForms) => {
        this.pendingForms = pendingForms;
        this.pendingFormsLoaded = true;
      },
      error: () => {
        console.error('No se pudieron cargar los formularios pendientes');
        this.pendingForms = [];
        this.pendingFormsLoaded = true;
      },
    });
  }

  get scheduleNotAssigned(): boolean {
    return this.applicantSchedule?.scheduleAssigned === false;
  }

  get workSchedule(): string | null {
    if (!this.applicantSchedule || this.scheduleNotAssigned) {
      return null;
    }
    return (
      this.applicantSchedule.firstWeeksSchedule?.find(
        (day) => day.title === this.WORKDAY_TITLE && day.schedule != null
      )?.schedule ?? null
    );
  }

  get hasNoEvents(): boolean {
    return !this.calendarEvents.length;
  }

  get todayEvent(): CalendarEvent | null {
    const today = this.toIsoDate(new Date());
    return this.calendarEvents.find((event) => event.date === today) ?? null;
  }

  get trainingIdMissing(): boolean {
    return this.hasLoaded && !this.trainingId;
  }

  setCalendarConfig(): void {
    const buttonText = {
      today: 'Hoy',
    };
    const buttonHints = {
      prev: 'Mes anterior',
      next: 'Mes siguiente',
      today: 'Hoy',
    };
    this.calendarOptions = {
      locale: 'es',
      initialView: 'dayGridMonth',
      eventClick: this.handleEventClick.bind(this),
      plugins: [dayGridPlugin, interactionPlugin],
      height: 600,
      buttonText: buttonText,
      buttonHints: buttonHints,
      weekNumberCalculation: 'ISO',
      editable: true,
      eventOrder: 'schedule',
      dayCellContent: (arg) => this.buildDayCellContent(arg.dayNumberText, arg.date),
    };
  }

  buildDayCellContent(dayNumberText: string, date: Date): { html: string } {
    const dayNumber = `<span class="fc-daygrid-day-number">${dayNumberText}</span>`;
    if (!this.attendanceRegisteredDates.has(this.toIsoDate(date))) {
      return { html: dayNumber };
    }
    return {
      html:
        `<span class="day-attendance material-icons" title="Asistencia registrada"` +
        ` aria-label="Asistencia registrada">check_circle</span>${dayNumber}`,
    };
  }

  toIsoDate(date: Date): string {
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
  }

  getCalendarEvents(trainingId: string): void {
    this.isLoading = true;
    this.calendarioTurnosService.getCalendarShifts(trainingId).subscribe({
      next: (response) => {
        this.isLoading = false;
        this.hasLoaded = true;
        try {
          this.buildCalendar(response?.applicantScheduleInfo ?? null);
        } catch (error) {
          console.error('No se pudo armar el calendario', error);
          this.openErrorModal();
        }
      },
      error: (error: HttpErrorResponse) => {
        this.isLoading = false;
        this.hasLoaded = true;
        this.applicantSchedule = null;
        this.calendarEvents = [];
        if (error.status === 400 || error.status === 404) {
          clearStoredTrainingId();
          this.trainingId = null;
          return;
        }
        this.openErrorModal();
      },
    });
  }

  convertDateFormat(date: string | null): string {
    const dateSplit = (date ?? '').split('-');
    return dateSplit.length === 3
      ? `${dateSplit[2]}-${dateSplit[1]}-${dateSplit[0]}`
      : '';
  }

  getClassType(title: string): ClassType | null {
    const normalizedTitle = (title ?? '').trim().toLowerCase();
    return this.ATTENDANCE_CLASS_TITLES[normalizedTitle] ?? null;
  }

  handleEventClick(e: any): void {
    const title: string = e.event.title;
    if (title !== this.REST_DAY_TITLE) {
      const props = e.event.extendedProps;
      this.dialog
        .open(TurnoModalComponent, {
          width: '400px',
          data: {
            props: props,
            classDate: e.event.startStr,
            classType: this.getClassType(title),
            trainingId: this.trainingId,
            attendanceRegistered: props.attendanceRegistered ?? null,
          },
        })
        .afterClosed()
        .subscribe((isAttendanceRegistered: boolean) => {
          if (isAttendanceRegistered && this.trainingId) {
            this.getCalendarEvents(this.trainingId);
          }
        });
    }
  }

  private buildCalendar(applicantSchedule: ApplicantSchedule | null): void {
    this.applicantSchedule = applicantSchedule;
    this.calendarEvents = this.buildEvents(applicantSchedule);
    this.attendanceRegisteredDates = new Set(
      this.calendarEvents
        .filter((event) => event.attendanceRegistered === true)
        .map((event) => event.date)
    );
    this.applyInitialDate();
  }

  private buildEvents(applicantSchedule: ApplicantSchedule | null): CalendarEvent[] {
    if (!applicantSchedule) {
      return [];
    }
    const events: CalendarEvent[] = (applicantSchedule.firstWeeksSchedule ?? [])
      .filter((day) => !!day?.date)
      .map((day) => ({ ...day, color: day.color ?? undefined }));

    if (applicantSchedule.inducementDate) {
      events.push({
        date: applicantSchedule.inducementDate,
        title: this.INDUCTION_TITLE,
        color: applicantSchedule.inducementColor ?? undefined,
        locationName: applicantSchedule.inducementLocationName,
        locationAddress: applicantSchedule.inducementLocationAddress,
        schedule: applicantSchedule.inducementSchedule,
        attendanceRegistered: applicantSchedule.inducementAttendanceRegistered,
      });
    }
    return events;
  }

  private applyInitialDate(): void {
    if (this.initialDateApplied) {
      return;
    }
    const dates = this.calendarEvents.map((event) => event.date).filter((date) => !!date);
    if (!dates.length) {
      return;
    }
    this.calendarOptions.initialDate = dates.reduce((a, b) => (a < b ? a : b));
    this.initialDateApplied = true;
  }

  private openErrorModal(): void {
    this.dialog.open(ErrorModalComponent, {
      width: '350px',
      disableClose: true,
    });
  }
}
